/** @jsx jsx */
import { jsx, Text } from "theme-ui";
import { useCallback, useContext } from "react";
import PropTypes from "prop-types";

import { flatten } from "sites-common/utils/lodash";

import { doFormatShortDate } from "@heartfulnessinstitute/react-hfn-forms/dist/formatting";
import { DefaultButton, Stack } from "office-ui-fabric-react";
import { DynamicFormLang, useLang } from "./LanguageSelector";
import FormContext from "./FormContext";
import { STAY_CHECKEDIN, STAY_CHECKEDOUT } from "./constants";

const as_per_passport = require("./json/frro/as_per_passport.json");
const passport_json = require("./json/frro/passport.json");
const visa_json = require("./json/frro/visa.json");
const arriving_from = require("./json/frro/arriving_from.json");
const arrival_departure = require("./json/frro/arrival_departure.json");
const contact_details = require("./json/frro/contact_details.json");
const next_destination = require("./json/frro/next_destination.json");

const validators = require("./json/frro/validators.json");

const { shade6, shade5, shade4 } = require("./json/colors.json");

const staticDefaults = {
  applicant_special_category: "0",
  applicant_purpovisit: "16",
  applicant_timeoarrivalhotel: "6",
};

const IndividualFormC = ({ defaults }) => {
  const {
    p2,
    onSaveFormC,
    discardFormC: onDiscard,
    admin,
  } = useContext(FormContext);
  const [getLangString] = useLang();

  const { record, sections = [], printOnly } = p2;

  const { formc, arrival, departure, passport_num, full_name, stay_status } =
    record;

  const locked =
    !admin &&
    (stay_status === STAY_CHECKEDIN || stay_status === STAY_CHECKEDOUT);

  const defaultValues = {
    applicant_permaddr: defaults.address,
    applicant_permcity: defaults.city,
    applicant_arrivedfromplace: defaults.address,
    applicant_arrivedfromcity: defaults.city,

    applicant_passpno: passport_num,
    applicant_doarrivalhotel: arrival,
    date_departure: departure,

    ...staticDefaults,
    ...formc,
  };

  const onSubmit = useCallback(
    (data, sc, fc) => {
      onSaveFormC(record, data, sc, fc);
    },
    [onSaveFormC, record]
  );

  const f = flatten(
    [
      sections.includes("passport") && as_per_passport,
      sections.includes("passport") && passport_json,
      sections.includes("visa") && visa_json,
      sections.includes("travel") && arriving_from,
      sections.includes("travel") && arrival_departure,
      sections.includes("contact") && contact_details,
      sections.includes("travel") && next_destination,
    ].filter((x) => x)
  );

  // console.log(f);

  return (
    <div>
      {printOnly && (
        <Stack horizontal sx={{ my: 4 }}>
          <DefaultButton
            iconProps={{ iconName: "Back" }}
            text={getLangString("strings.go_back")}
            onClick={onDiscard}
          />
        </Stack>
      )}

      <div sx={{ backgroundColor: shade5, marginBottom: "1px", p: 2 }}>
        <Stack horizontalAlign="center">
          <Text variant="title">
            {getLangString("strings.formc")} - {full_name}
          </Text>
          <Stack.Item>
            <Text variant="subtitle">
              {doFormatShortDate(arrival)} - {doFormatShortDate(departure)}
            </Text>
          </Stack.Item>
        </Stack>
      </div>
      <div sx={{ backgroundColor: shade6, p: 2 }}>
        <small>{getLangString("strings.title_note")}</small>
      </div>
      <div sx={{ backgroundColor: shade4, p: 2 }}>
        <DynamicFormLang
          formFields={f}
          formValidator={validators}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          onDiscard={onDiscard}
          printOnly={locked || printOnly}
        />
      </div>
      {printOnly && (
        <Stack horizontal sx={{ my: 4 }}>
          <DefaultButton
            iconProps={{ iconName: "Back" }}
            text={getLangString("strings.go_back")}
            onClick={onDiscard}
          />
        </Stack>
      )}
    </div>
  );
};

IndividualFormC.propTypes = {
  defaults: PropTypes.shape({
    city: PropTypes.string,
    address: PropTypes.string,
  }),
};
IndividualFormC.defaultProps = {
  defaults: {
    city: "12",
    address: "",
  },
};

export default IndividualFormC;
